import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Users, MessageCircle, Calendar, Sparkles, Crown, ChevronRight, ShieldCheck, Sun, Cloud, CloudRain, Snowflake, Thermometer, Baby, MapPin, Accessibility, Star, Eye, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import axios from "axios";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/j9ko1qni_70964783-6509-4567-B714-2BDADDC4579E%20%281%29.PNG";

const WEATHER_OPTIONS = [
  { value: "sunny", label: "Sunny", icon: Sun, color: "text-amber-500" },
  { value: "moderate", label: "Mild", icon: Cloud, color: "text-slate-500" },
  { value: "rainy", label: "Rainy", icon: CloudRain, color: "text-blue-500" },
  { value: "cold", label: "Cold", icon: Snowflake, color: "text-cyan-500" },
  { value: "hot", label: "Hot", icon: Thermometer, color: "text-red-500" },
];

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityContext, setActivityContext] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [selectedWeather, setSelectedWeather] = useState("moderate");
  
  // Admin preview mode - simulate free user
  const isAdminPreview = localStorage.getItem("admin_preview_free") === "true";
  const effectiveIsPremium = isAdminPreview ? false : user?.is_premium;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, matchesRes] = await Promise.all([
          axios.get(`${API}/profile`).catch(() => null),
          axios.get(`${API}/matches`).catch(() => ({ data: { matches: [] } }))
        ]);
        
        if (profileRes?.data) {
          setProfile(profileRes.data);
        } else {
          navigate('/onboarding');
          return;
        }
        setMatches(matchesRes.data.matches || []);
      } catch (error) {
        console.error('Dashboard error:', error);
      }
    };
    fetchData();
  }, [navigate]);

  const fetchActivities = async (weather) => {
    setLoadingActivities(true);
    try {
      const response = await axios.post(`${API}/ai/activity-suggestions`, { weather });
      setActivities(response.data.activities || []);
      setActivityContext(response.data.context || null);
    } catch (error) {
      console.error('Activities error:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center pb-20">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF9F5] via-[#FFF5F8] to-[#F5F0FF] pb-24" data-testid="dashboard">
      {/* Admin Preview Banner */}
      {isAdminPreview && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-center text-sm font-medium sticky top-0 z-50">
          <span className="flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            Admin Preview Mode: Viewing as FREE user
            <button 
              onClick={() => {
                localStorage.removeItem("admin_preview_free");
                window.location.reload();
              }}
              className="ml-4 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs"
            >
              Exit Preview
            </button>
          </span>
        </div>
      )}
      
      {/* Decorative blobs with shimmer */}
      <div className="blob blob-pink w-64 h-64 -top-32 -right-32 fixed opacity-40 animate-pulse" />
      <div className="blob blob-blue w-48 h-48 top-1/2 -left-24 fixed opacity-40" />
      <div className="fixed top-20 right-10 w-32 h-32 bg-gradient-to-br from-purple-300/20 to-pink-300/20 rounded-full blur-xl animate-float" />
      <div className="fixed bottom-40 left-10 w-24 h-24 bg-gradient-to-br from-teal-300/20 to-cyan-300/20 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }} />

      {/* Header with gradient */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 backdrop-blur-lg border-b border-purple-200/50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img src={LOGO_URL} alt="KiddiPlay" className="w-12 h-12 rounded-full object-cover border-2 border-purple-200 shadow-lg" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-teal-400 rounded-full border-2 border-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Hi, {profile.parent_name?.split(' ')[0]}!
                </h1>
                <p className="text-sm text-purple-500">Ready to find playdates?</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="ghost" className="text-slate-500 hover:bg-purple-100 rounded-full px-3 py-2 text-sm" data-testid="homepage-btn">
                  <Home className="w-4 h-4 mr-1" />
                  Homepage
                </Button>
              </Link>
              {!effectiveIsPremium && (
                <Link to="/subscription">
                  <Button className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-full px-4 py-2 text-sm shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="upgrade-btn">
                    <Crown className="w-4 h-4 mr-1" />
                    Upgrade
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Quick Actions - More Vibrant */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Link to="/discover">
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-purple-100/50 hover:border-purple-300" data-testid="discover-card">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Discover</h3>
              <p className="text-sm text-purple-500">Find new families</p>
            </div>
          </Link>
          <Link to="/messages">
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-pink-100/50 hover:border-pink-300" data-testid="messages-card">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-pink-600 to-pink-800 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Messages</h3>
              <p className="text-sm text-pink-500">Chat with matches</p>
            </div>
          </Link>
          <Link to="/buddy-up">
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-orange-100/50 hover:border-orange-300" data-testid="sitter-pool-card">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Baby className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-orange-600 to-orange-800 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Buddy UP!</h3>
              <p className="text-sm text-orange-500">Share activities & costs</p>
            </div>
          </Link>
          <Link to="/playdates">
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-teal-100/50 hover:border-teal-300" data-testid="playdates-card">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Playdates</h3>
              <p className="text-sm text-teal-500">Plan meetups</p>
            </div>
          </Link>
          <Link to="/venues">
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-rose-100/50 hover:border-rose-300" data-testid="marketplace-card">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-rose-600 to-rose-800 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Venues</h3>
              <p className="text-sm text-rose-500">Find fun places</p>
            </div>
          </Link>
          <Link to="/niamhs-corner">
            <div className="group bg-gradient-to-br from-amber-50 to-yellow-50 backdrop-blur-sm rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer border border-amber-200/50 hover:border-amber-400 relative overflow-hidden" data-testid="top-activities-card">
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-400/20 to-yellow-400/20 rounded-full blur-xl" />
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent" style={{ fontFamily: 'Nunito, sans-serif' }}>Top Activities</h3>
              <p className="text-sm text-amber-600">Play CEO picks!</p>
            </div>
          </Link>
        </div>

        {/* Your Matches */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Your Matches
            </h2>
            <Link to="/discover" className="text-teal-500 text-sm font-medium flex items-center">
              Find More <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {matches.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {matches.slice(0, 5).map((match) => (
                <Link key={match.match_id} to={`/messages`} className="flex-shrink-0">
                  <div className="w-20 text-center">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-2 ring-4 ring-teal-100 overflow-hidden ${match.profile?.is_blurred ? 'bg-gradient-to-br from-sky-400 to-purple-400' : ''}`}>
                      {!match.profile?.is_blurred && match.profile?.photo_url ? (
                        <img src={match.profile.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                          {match.profile?.parent_name?.[0] || '?'}
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-700 truncate">{match.profile?.parent_name?.split(' ')[0]}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-4">No matches yet. Start discovering families!</p>
              <Link to="/discover">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full" data-testid="start-discovering-btn">
                  Start Discovering
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* AI Activity Suggestions */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Activity Ideas
              </h2>
            </div>
          </div>
          
          {/* Weather Selector */}
          <div className="mb-4">
            <p className="text-sm text-slate-500 mb-2">What's the weather like?</p>
            <div className="flex flex-wrap gap-2">
              {WEATHER_OPTIONS.map((weather) => {
                const Icon = weather.icon;
                return (
                  <button
                    key={weather.value}
                    onClick={() => setSelectedWeather(weather.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedWeather === weather.value
                        ? 'bg-teal-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`weather-${weather.value}`}
                  >
                    <Icon className={`w-4 h-4 ${selectedWeather === weather.value ? 'text-white' : weather.color}`} />
                    {weather.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            onClick={() => fetchActivities(selectedWeather)}
            disabled={loadingActivities}
            className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-xl mb-4"
            data-testid="get-ideas-btn"
          >
            {loadingActivities ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Getting ideas...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Get Personalized Ideas
              </span>
            )}
          </Button>

          {/* Context info */}
          {activityContext && (
            <div className="mb-4 p-3 bg-sky-50 rounded-xl text-sm">
              <p className="text-sky-700">
                <span className="font-medium">Suggestions for:</span> {activityContext.season} season
                {activityContext.upcoming_holidays?.length > 0 && (
                  <span> • Upcoming: {activityContext.upcoming_holidays.join(", ")}</span>
                )}
              </p>
              {activityContext.allergens_considered?.length > 0 && (
                <p className="text-amber-600 mt-1">
                  <span className="font-medium">Allergens considered:</span> {activityContext.allergens_considered.join(", ")}
                </p>
              )}
            </div>
          )}

          {activities.length > 0 ? (
            <div className="space-y-3">
              {activities.slice(0, 4).map((activity, i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-2xl">
                  <div className="flex items-start justify-between">
                    <h4 className="font-semibold text-slate-800">{activity.title}</h4>
                    {activity.indoor_outdoor && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        activity.indoor_outdoor === 'indoor' ? 'bg-purple-100 text-purple-600' :
                        activity.indoor_outdoor === 'outdoor' ? 'bg-green-100 text-green-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {activity.indoor_outdoor}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{activity.description}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {activity.age_range && (
                      <span className="text-xs bg-teal-100 text-teal-600 px-2 py-1 rounded-full">
                        Ages: {activity.age_range}
                      </span>
                    )}
                    {activity.estimated_duration && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                        {activity.estimated_duration}
                      </span>
                    )}
                    {activity.seasonal_theme && (
                      <span className="text-xs bg-amber-100 text-amber-600 px-2 py-1 rounded-full">
                        {activity.seasonal_theme}
                      </span>
                    )}
                  </div>
                  {activity.dietary_note && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <span>✓</span> {activity.dietary_note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-slate-500">Get personalized activity ideas based on weather, season, holidays, and your family's dietary needs!</p>
            </div>
          )}
        </div>

        {/* Premium CTA (for free users) */}
        {!user?.is_premium && (
          <Link to="/subscription">
            <div className="premium-gradient rounded-3xl p-6 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer" data-testid="premium-cta">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                  <Crown className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>Go Premium</h3>
                  <p className="opacity-90">Unlimited matches, see photos, plan playdates</p>
                </div>
                <ChevronRight className="w-6 h-6" />
              </div>
            </div>
          </Link>
        )}

        {/* Profile Status */}
        <div className="bg-white rounded-3xl p-6 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Your Family</h3>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-400 to-purple-400 flex items-center justify-center text-white text-2xl font-bold">
              {profile.parent_name?.[0]}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-800">{profile.parent_name}</p>
                {profile.is_verified && (
                  <ShieldCheck className="w-5 h-5 text-teal-500" />
                )}
              </div>
              <p className="text-sm text-slate-500">{profile.location_city}</p>
              <p className="text-sm text-slate-600">
                {profile.children?.length || 0} kid{(profile.children?.length || 0) !== 1 ? 's' : ''}
              </p>
            </div>
            <Link to="/profile">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="edit-profile-btn">
                Edit
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default Dashboard;